/**
 * ViewNudge — A thin, dismissible contextual prompt at the top of the chat.
 * Shows a view-specific, data-aware shortcut ("Draft 6 of 9 empty BMC sections",
 * "Analyze my 12 competitors"). Copy is computed by the shared buildNudgeCopy
 * so the chat auto-greet uses the same wording.
 */

import { useMemo, useState } from 'react'
import { Sparkles, X } from 'lucide-react'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { buildNudgeCopy } from '@/lib/skerpa/autoGreet'

interface ViewNudgeProps {
  onSend: (text: string) => void
  isStreaming: boolean
}

export function ViewNudge({ onSend, isStreaming }: ViewNudgeProps) {
  const activeView = useSkerpaContextStore(s => s.activeView)
  const pageContext = useSkerpaContextStore(s => s.pageContext)
  const canvasSectionState = useSkerpaContextStore(s => s.canvasSectionState)
  const getDomainScores = useJourneyStore(s => s.getDomainScores)
  const getCurrentPhase = useJourneyStore(s => s.getCurrentPhase)
  const [dismissedKey, setDismissedKey] = useState<string | null>(null)

  const nudge = useMemo(() => {
    const phase = getCurrentPhase()
    return buildNudgeCopy({
      activeView,
      pageContext: pageContext || '',
      canvasSectionState,
      domainScores: getDomainScores(),
      currentPhaseName: phase?.name || null,
      currentPhaseDomain: phase?.domain || null,
    })
  }, [activeView, pageContext, canvasSectionState, getDomainScores, getCurrentPhase])

  const nudgeKey = nudge ? `${activeView}:${nudge.label}` : null

  if (!nudge || isStreaming || dismissedKey === nudgeKey) return null

  return (
    <div className="group flex items-center border-b border-border-subtle hover:bg-warm-50 transition-colors">
      <button
        type="button"
        onClick={() => onSend(nudge.prompt)}
        className="flex min-w-0 flex-1 items-center gap-2 px-4 py-2 text-left"
      >
        <Sparkles className="w-3 h-3 text-text-muted flex-shrink-0" />
        <span className="text-[12px] text-text-secondary font-medium truncate">{nudge.label}</span>
      </button>
      <button
        type="button"
        aria-label="Dismiss suggestion"
        onClick={() => setDismissedKey(nudgeKey)}
        className="mr-2 flex h-5 w-5 items-center justify-center rounded text-text-muted opacity-0 transition-opacity group-hover:opacity-100 hover:bg-warm-100"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}
